import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Banner, Button } from "@cinesys/ui/components";
import DelayedSpinner from "../../components/DelayedSpinner";
import {
  loadRemoteApp,
  type LoaderDeps,
  type RemoteMountOptions,
} from "./loadRemoteApp";

// The remote fills this box and scrolls inside it, so it needs a definite height.
const Host = styled.div`
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const ErrorWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 24px;
  text-align: center;
`;

export interface RemoteAppErrorPanelProps {
  name: string;
  onRetry: () => void;
}

export const RemoteAppErrorPanel = ({
  name,
  onRetry,
}: RemoteAppErrorPanelProps) => (
  <ErrorWrap role="alert">
    <Banner error={`${name} could not be loaded.`} />
    <Button label="Retry" onClick={onRetry} />
  </ErrorWrap>
);

export interface RemoteAppProps {
  /** Shown in the error panel. */
  name: string;
  manifestPath: string;
  options: RemoteMountOptions;
  /** Rendered instead of the error panel when loading or mounting fails. */
  fallback?: (error: Error, retry: () => void) => React.ReactNode;
  deps?: LoaderDeps;
  /** A load that hasn't mounted by then counts as failed. */
  timeoutMs?: number;
}

export const DEFAULT_REMOTE_TIMEOUT_MS = 15_000;

type Phase =
  | { kind: "loading" }
  | { kind: "mounted" }
  | { kind: "failed"; error: Error };

/**
 * Loads a remote-app bundle and mounts it into its own host element for as long as this
 * component is mounted. The remote renders in its own React root, so nothing inside the host
 * element is managed by this tree.
 */
const RemoteApp = ({
  name,
  manifestPath,
  options,
  fallback,
  deps,
  timeoutMs = DEFAULT_REMOTE_TIMEOUT_MS,
}: RemoteAppProps) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [attempt, setAttempt] = useState(0);
  // Read at mount time only; a parent re-render must not remount the remote.
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const depsRef = useRef(deps);

  useEffect(() => {
    let cancelled = false;
    let teardown: (() => void) | undefined;
    const controller = new AbortController();
    setPhase({ kind: "loading" });

    const fail = (e: unknown) => {
      cancelled = true;
      controller.abort();
      const error = e instanceof Error ? e : new Error(String(e));
      console.error(`[remote-app] ${name} failed to load:`, error);
      setPhase({ kind: "failed", error });
    };
    // setTimeout fires immediately for anything past 2^31 - 1 ms.
    const waitMs = Number.isNaN(timeoutMs)
      ? DEFAULT_REMOTE_TIMEOUT_MS
      : Math.min(Math.max(timeoutMs, 0), 2 ** 31 - 1);
    const timer = setTimeout(
      () => fail(new Error(`not loaded after ${waitMs}ms`)),
      waitMs
    );

    loadRemoteApp(manifestPath, depsRef.current, {
      signal: controller.signal,
      attempt,
    })
      .then(({ module, removeCss }) => {
        if (cancelled) {
          removeCss();
          return;
        }
        clearTimeout(timer);
        const host = hostRef.current;
        if (!host) {
          removeCss();
          throw new Error("host element is not rendered");
        }
        let unmount: () => void;
        try {
          unmount = module.mount(host, optionsRef.current);
        } catch (e) {
          removeCss();
          throw e;
        }
        teardown = () => {
          try {
            unmount();
          } finally {
            removeCss();
          }
        };
        setPhase({ kind: "mounted" });
      })
      .catch((e: unknown) => {
        clearTimeout(timer);
        if (!cancelled) {
          fail(e);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timer);
      teardown?.();
    };
  }, [manifestPath, name, attempt, timeoutMs]);

  if (phase.kind === "failed") {
    const retry = () => {
      setPhase({ kind: "loading" });
      setAttempt((n) => n + 1);
    };
    return (
      <>
        {fallback ? (
          fallback(phase.error, retry)
        ) : (
          <RemoteAppErrorPanel name={name} onRetry={retry} />
        )}
      </>
    );
  }

  return (
    <>
      {phase.kind === "loading" && <DelayedSpinner padTop />}
      <Host ref={hostRef} data-remote-app={name} />
    </>
  );
};

export default RemoteApp;
